import type { LighterAccountIndex } from '@funkit/api-base'
import { useLogOnce } from '@funkit/connect-core'
import { useLighterFastUnstakedFeeTokens } from '@funkit/connect-core/clients/lighter'

import { logger } from '../../src/logging/logger'

/**
 * The Lighter Fast "unstaked fee": a flat deduction taken off the withdrawal
 * before quoting, waived for users staked at or above Lighter's own threshold.
 *
 * Reads Lighter's `transferFeeInfo` endpoint (via `useLighterFastUnstakedFeeTokens`)
 * rather than deriving the fee from share price locally — Lighter's backend
 * already knows the account's stake. `feeTokens` is 0 while the lookup is in
 * flight and for every non-Fast caller; a failed lookup fails closed to the
 * flat historical fee rather than 0. Callers must wait for `isFetched` before
 * quoting off `feeTokens` — see `quoteInputFeeSettled`.
 */
export function useLighterUnstakedFee({
  accountIndex,
  enabled,
}: {
  accountIndex: string | undefined
  /** Fast only — Secure charges no such fee. */
  enabled: boolean
}): {
  feeTokens: number
  /** `true` for a non-Fast caller: nothing to wait on. */
  isFetched: boolean
} {
  const isEnabled = enabled && !!accountIndex
  const { feeTokens, isFetched, isError } = useLighterFastUnstakedFeeTokens({
    accountIndex: isEnabled ? (accountIndex as LighterAccountIndex) : undefined,
    enabled: isEnabled,
  })

  useLogOnce(
    isEnabled && isFetched
      ? `${accountIndex}|${isError}|${feeTokens}`
      : undefined,
    () => {
      if (isError) {
        // The fee is charged from Lighter's own view of the stake, which we
        // could not read — so it is applied blind and worth surfacing.
        logger.warn('lighter:fast:unstaked-fee:account-unavailable', {
          accountIndex,
          unstakedFeeTokens: feeTokens,
        })
        return
      }
      logger.info('lighter:fast:unstaked-fee:resolved', {
        accountIndex,
        unstakedFeeTokens: feeTokens,
      })
    },
  )

  return { feeTokens, isFetched: !isEnabled || isFetched }
}
